import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { LuTrash2 } from "react-icons/lu";
import { api, ApiError , errMsg } from "../lib/api";
import { useActiveFlat } from "../lib/active-flat";
import { useAuth } from "../lib/auth";
import type { Notice, NoticeTag } from "../lib/types";
import { Avatar } from "../components/Avatar";
import {
  Button,
  Card,
  CenteredSpinner,
  EmptyState,
  ErrorBanner,
  IconButton,
  Input,
  Label,
  Pill,
  SectionHeader,
  Textarea,
} from "../components/ui";
import { formatRelativeTime } from "../lib/format";

const TAG_OPTIONS: NoticeTag[] = ["GENERAL", "URGENT", "SOCIAL", "RULES"];

const TAG_TONE: Record<NoticeTag, "rose" | "violet" | "slate" | "brand"> = {
  URGENT: "rose",
  SOCIAL: "violet",
  RULES: "slate",
  GENERAL: "brand",
};

const TAG_LABEL: Record<NoticeTag, string> = {
  URGENT: "Urgent",
  SOCIAL: "Social",
  RULES: "Rules",
  GENERAL: "General",
};

export function BoardPage() {
  const { flat } = useActiveFlat();
  const { user } = useAuth();
  const flatId = flat!.id;

  const [notices, setNotices] = useState<Notice[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [tag, setTag] = useState<NoticeTag>("GENERAL");
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await api.get<Notice[]>(`/notice/by-flat/${flatId}`);
      setNotices(data);
    } catch (err) {
      setError(errMsg(err, "Couldn't load notices."));
    }
  }, [flatId]);

  useEffect(() => {
    load();
  }, [load]);

  const post = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return setError("Add a title and a message.");
    setPosting(true);
    try {
      const notice = await api.post<Notice>(`/notice/by-flat/${flatId}`, {
        title: title.trim(),
        body: body.trim(),
        tag,
      });
      setNotices((prev) => (prev ? [notice, ...prev] : [notice]));
      setTitle("");
      setBody("");
      setTag("GENERAL");
      setError(null);
    } catch (err) {
      setError(errMsg(err, "Couldn't post notice."));
    } finally {
      setPosting(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm("Delete this notice?")) return;
    try {
      await api.delete(`/notice/${id}`);
      setNotices((prev) => prev?.filter((n) => n.id !== id) ?? null);
    } catch (err) {
      setError(errMsg(err, "Couldn't delete."));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Board</h1>
        <p className="text-sm text-slate-500">House notices, plans, and rules.</p>
      </div>

      <ErrorBanner message={error} />

      <Card>
        <SectionHeader title="Post a notice" />
        <form onSubmit={post} className="space-y-3">
          <div>
            <Label htmlFor="ntitle">Title</Label>
            <Input
              id="ntitle"
              placeholder="Plumber visit on Saturday"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={posting}
            />
          </div>
          <div>
            <Label htmlFor="nbody">Message</Label>
            <Textarea
              id="nbody"
              rows={3}
              placeholder="Add the details..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={posting}
            />
          </div>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <Label>Tag</Label>
              <div className="flex flex-wrap gap-2">
                {TAG_OPTIONS.map((t) => (
                  <button
                    type="button"
                    key={t}
                    onClick={() => setTag(t)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                      tag === t
                        ? "ring-2 ring-offset-2 ring-brand-500"
                        : "ring-1 ring-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <Pill tone={TAG_TONE[t]}>{TAG_LABEL[t]}</Pill>
                  </button>
                ))}
              </div>
            </div>
            <Button type="submit" loading={posting}>
              Post notice
            </Button>
          </div>
        </form>
      </Card>

      {notices === null ? (
        <CenteredSpinner />
      ) : notices.length === 0 ? (
        <EmptyState title="No notices yet" description="Be the first to post one." />
      ) : (
        <motion.div
          className="space-y-3"
          initial="hidden"
          animate="show"
          variants={{ show: { transition: { staggerChildren: 0.06 } } }}
        >
          <AnimatePresence initial={false}>
          {notices.map((n) => (
            <motion.article
              key={n.id}
              layout
              variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { duration: 0.22 } } }}
              exit={{ opacity: 0, scale: 0.97, transition: { duration: 0.15 } }}
              className={`rounded-xl border bg-white p-5 shadow-sm ${
                n.tag === "URGENT" ? "border-rose-200 ring-1 ring-rose-100" : "border-slate-200"
              }`}
            >
              <div className="mb-2 flex items-start justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Pill tone={TAG_TONE[n.tag]}>{TAG_LABEL[n.tag]}</Pill>
                  <h3 className="text-base font-semibold text-slate-900">{n.title}</h3>
                </div>
                {n.authorId === user!.id && (
                  <IconButton icon={<LuTrash2 className="h-4 w-4" />} label="Delete notice" onClick={() => remove(n.id)} variant="danger" />
                )}
              </div>
              <p className="whitespace-pre-line text-sm text-slate-700">{n.body}</p>
              <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3 text-xs text-slate-500">
                <Avatar name={n.author.name} colour={n.author.colour} size="xs" />
                <span className="font-medium text-slate-700">{n.author.name}</span>
                <span>·</span>
                <span>{formatRelativeTime(n.createdAt)}</span>
              </div>
            </motion.article>
          ))}
          </AnimatePresence>
        </motion.div>
      )}
    </div>
  );
}
